import { useState } from "react"
import { FileDown, Trash2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useUpdateUsinageWaste } from "@/features/milling/hooks/use-milling-mutations"
import { formatNumber, kgToMt } from "@/lib/format"
import { cn } from "@/lib/utils"

// Intercepts a rapport download when the operator hasn't recorded
// the run-wide déchet (waste) yet. The Freinte rapport closes its
// Ecart = engaged − output − déchet reconciliation against this
// value, so silently downloading with déchet=0 would publish a
// rapport that overstates the freinte%.
//
// Behaviour:
//   - Opens when the operator triggers a rapport download AND
//     wasteKg is still 0 on the cached usinage.
//   - "Save & download" persists the entered kg via PATCH /waste,
//     then triggers the deferred download in onSuccess.
//   - "Download without déchet" skips the save and runs the
//     download as-is (the report reads déchet = 0 kg).
//   - Cancel closes without saving or downloading.
interface WasteRequiredDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  usinageId: string | number
  onProceed: () => void
}

export function WasteRequiredDialog({
  open,
  onOpenChange,
  usinageId,
  onProceed,
}: WasteRequiredDialogProps) {
  const { t } = useTranslation("milling")
  const updateWaste = useUpdateUsinageWaste(usinageId)
  const [wasteKg, setWasteKg] = useState<string>("")

  const trimmed = wasteKg.trim()
  const parsed = Number(wasteKg)
  const valid = trimmed === "" || (!Number.isNaN(parsed) && parsed >= 0)
  const numericValue = trimmed === "" ? 0 : parsed
  const canSave = valid && numericValue > 0

  const onSaveAndDownload = () => {
    if (!canSave) return
    updateWaste.mutate(
      { wasteKg: numericValue },
      {
        onSuccess: () => {
          onOpenChange(false)
          onProceed()
          setWasteKg("")
        },
      },
    )
  }

  const onSkip = () => {
    onOpenChange(false)
    onProceed()
    setWasteKg("")
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Trash2 className="size-4 text-muted-foreground" />
            {t("wasteDialog.title")}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {t("wasteDialog.description")}
          </DialogDescription>
        </DialogHeader>

        {/* Formula reminder — surfaces the equation the operator's
            value is plugging into. Reading the formula explains the
            prompt better than a long paragraph would. */}
        <div className="rounded-md border border-dashed bg-muted/30 px-3 py-2 text-[11px] tabular-nums text-muted-foreground">
          {t("wasteDialog.formula")}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label
            htmlFor="waste-dialog-kg"
            className="text-xs font-medium text-muted-foreground"
          >
            {t("wasteDialog.fieldLabel")}
          </Label>
          {/* Input + unit suffix in a single bordered shell so the
              caret and "kg" sit together. Width is bounded so the
              shell doesn't stretch and push the suffix to the far
              right of the dialog. Focus styles bubble up via
              `focus-within:` so tabbing in highlights the whole
              control. */}
          <div
            className={cn(
              "flex h-10 w-full max-w-[240px] items-center rounded-md border border-input bg-transparent pr-3 shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50",
              !valid &&
                "border-destructive focus-within:border-destructive focus-within:ring-destructive/30",
            )}
          >
            <Input
              id="waste-dialog-kg"
              autoFocus
              type="number"
              inputMode="decimal"
              min={0}
              step="1"
              value={wasteKg}
              onChange={(e) => setWasteKg(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSave && !updateWaste.isPending) {
                  e.preventDefault()
                  onSaveAndDownload()
                }
              }}
              placeholder="0"
              aria-invalid={!valid}
              className="h-full flex-1 border-0 bg-transparent px-3 text-lg font-semibold tabular-nums shadow-none focus-visible:border-0 focus-visible:ring-0 aria-invalid:border-0 aria-invalid:ring-0"
            />
            <span className="border-l pl-3 text-xs font-medium text-muted-foreground">
              kg
            </span>
          </div>
          <p
            className={cn(
              "min-h-[14px] text-[11px] tabular-nums",
              valid ? "text-muted-foreground" : "text-destructive",
            )}
          >
            {trimmed === ""
              ? t("wasteDialog.hint")
              : valid
                ? t("wasteDialog.equivalent", {
                    mt: formatNumber(kgToMt(numericValue), 3),
                  })
                : t("wasteDialog.invalid")}
          </p>
        </div>

        {/* Escape hatch as a quiet text link above the footer — the
            footer keeps only the primary action pair so the two
            buttons never get squeezed in French (where the CTA
            copy is longest). */}
        <button
          type="button"
          onClick={onSkip}
          disabled={updateWaste.isPending}
          className="-mt-2 self-start text-[11px] font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
        >
          {t("wasteDialog.skip")}
        </button>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={updateWaste.isPending}
          >
            {t("wasteDialog.cancel")}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={onSaveAndDownload}
            disabled={!canSave || updateWaste.isPending}
          >
            <FileDown className="size-4" />
            {updateWaste.isPending
              ? t("wasteDialog.saving")
              : t("wasteDialog.saveAndDownload")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
